<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('GCC role mappings') }}</h1>
                    <p class="text-sm text-gray-500 mt-1">
                        {{ $t('Map GCC roles to ticketing roles. Changes apply on the next login of each user.') }}
                    </p>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <button class="btn btn-blue shadow-sm rounded-md" type="button" @click="openModal()">
                        {{ $t('New mapping') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6 bg-white shadow overflow-hidden sm:rounded-md">
                <loading :status="loading"/>
                <template v-if="items.length > 0">
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('GCC role') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Ticketing role') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Manager role') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Department') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Position') }}</th>
                                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Actions') }}</th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="item in items" :key="item.id">
                                <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ item.gcc_role_name }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">{{ item.user_role ? item.user_role.name : '-' }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">
                                    <template v-if="item.manager_user_role_id">{{ managerRoleName(item.manager_user_role_id) }}</template>
                                    <template v-else>-</template>
                                </td>
                                <td class="px-4 py-3 text-sm text-gray-700">{{ item.department ? item.department.name : '-' }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">
                                    <template v-if="item.position === 'manager'">{{ $t('Manager') }}</template>
                                    <template v-else-if="item.position === 'staff'">{{ $t('Staff') }}</template>
                                    <template v-else>{{ $t('Ambiguous') }}</template>
                                </td>
                                <td class="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        class="btn btn-secondary shadow-sm rounded-md mr-2"
                                        type="button"
                                        @click="openModal(item)"
                                    >
                                        {{ $t('Edit') }}
                                    </button>
                                    <button
                                        class="btn btn-red shadow-sm rounded-md"
                                        type="button"
                                        @click="openDeleteModal(item)"
                                    >
                                        {{ $t('Delete') }}
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-else-if="!loading">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <div v-show="modal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity">
                    <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closeModal"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div
                    aria-labelledby="mapping-modal-headline"
                    aria-modal="true"
                    class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                    role="dialog"
                >
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 id="mapping-modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                            {{ form.id ? $t('Edit mapping') : $t('New mapping') }}
                        </h3>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mapping-gcc-role">{{ $t('GCC role') }}</label>
                            <input
                                id="mapping-gcc-role"
                                v-model="form.gcc_role_name"
                                class="form-input block w-full mt-1 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                type="text"
                            >
                            <p class="mt-1 text-xs text-gray-500">{{ $t('Role name exactly as sent by GCC') }}.</p>
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mapping-user-role">{{ $t('Ticketing role') }}</label>
                            <select
                                id="mapping-user-role"
                                v-model="form.user_role_id"
                                class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                            >
                                <option :value="null" disabled>{{ $t('Select an option') }}</option>
                                <option v-for="userRole in userRoles" :value="userRole.id">{{ userRole.name }}</option>
                            </select>
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mapping-position">{{ $t('Position') }}</label>
                            <select
                                id="mapping-position"
                                v-model="form.position"
                                class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                            >
                                <option :value="null">{{ $t('Ambiguous') }}</option>
                                <option value="staff">{{ $t('Staff') }}</option>
                                <option value="manager">{{ $t('Manager') }}</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500">{{ $t('Ambiguous roles allow admins to toggle each user between staff and manager on the Mirror users page') }}.</p>
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mapping-manager-role">{{ $t('Manager role') }}</label>
                            <select
                                id="mapping-manager-role"
                                v-model="form.manager_user_role_id"
                                class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                            >
                                <option :value="null">{{ $t('None') }}</option>
                                <option v-for="userRole in userRoles" :value="userRole.id">{{ userRole.name }}</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500">{{ $t('Required for ambiguous roles') }}.</p>
                        </div>
                        <div class="mt-4">
                            <label class="block text-sm font-medium leading-5 text-gray-700" for="mapping-department">{{ $t('Department') }}</label>
                            <select
                                id="mapping-department"
                                v-model="form.department_id"
                                class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                            >
                                <option :value="null">{{ $t('None') }}</option>
                                <option v-for="department in departments" :value="department.id">{{ department.name }}</option>
                            </select>
                            <p class="mt-1 text-xs text-gray-500">{{ $t('Applied when the user mirror is first created') }}.</p>
                        </div>
                    </div>
                    <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            class="btn btn-blue shadow-sm rounded-md sm:ml-2"
                            :disabled="saving"
                            type="button"
                            @click="save"
                        >
                            {{ $t('Save') }}
                        </button>
                        <button
                            class="btn btn-secondary shadow-sm rounded-md"
                            type="button"
                            @click="closeModal"
                        >
                            {{ $t('Cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-show="deleteModal" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity">
                    <div class="absolute inset-0 bg-gray-500 opacity-75" @click="closeDeleteModal"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div
                    aria-labelledby="delete-modal-headline"
                    aria-modal="true"
                    class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
                    role="dialog"
                >
                    <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 id="delete-modal-headline" class="text-lg leading-6 font-medium text-gray-900">
                            {{ $t('Delete mapping') }}
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            {{ $t('Users with this GCC role will not be able to log in until a mapping is created again') }}.
                        </p>
                    </div>
                    <div class="bg-gray-100 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            class="btn btn-red shadow-sm rounded-md sm:ml-2"
                            :disabled="deleting"
                            type="button"
                            @click="destroy"
                        >
                            {{ $t('Delete') }}
                        </button>
                        <button
                            class="btn btn-secondary shadow-sm rounded-md"
                            type="button"
                            @click="closeDeleteModal"
                        >
                            {{ $t('Cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "gcc-role-mapping-list",
    metaInfo() {
        return {
            title: this.$i18n.t('GCC role mappings')
        }
    },
    data() {
        return {
            loading: true,
            saving: false,
            deleting: false,
            modal: false,
            deleteModal: false,
            items: [],
            userRoles: [],
            departments: [],
            form: {
                id: null,
                gcc_role_name: '',
                user_role_id: null,
                manager_user_role_id: null,
                department_id: null,
                position: null,
            },
        }
    },
    mounted() {
        this.getMappings();
        this.getUserRoles();
        this.getDepartments();
    },
    methods: {
        getMappings() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/admin/gcc-role-mappings').then(function (response) {
                self.items = response.data.items;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        getUserRoles() {
            const self = this;
            axios.get('api/dashboard/admin/settings/user-roles').then(function (response) {
                self.userRoles = response.data;
            });
        },
        getDepartments() {
            const self = this;
            axios.get('api/dashboard/admin/departments').then(function (response) {
                self.departments = response.data;
            });
        },
        managerRoleName(userRoleId) {
            const role = this.userRoles.find(function (userRole) {
                return userRole.id === userRoleId;
            });
            return role ? role.name : '-';
        },
        openModal(item) {
            this.form = item ? {
                id: item.id,
                gcc_role_name: item.gcc_role_name,
                user_role_id: item.user_role_id,
                manager_user_role_id: item.manager_user_role_id,
                department_id: item.department_id,
                position: item.position,
            } : {
                id: null,
                gcc_role_name: '',
                user_role_id: null,
                manager_user_role_id: null,
                department_id: null,
                position: null,
            };
            this.modal = true;
        },
        closeModal() {
            this.modal = false;
        },
        openDeleteModal(item) {
            this.form.id = item.id;
            this.form.gcc_role_name = item.gcc_role_name;
            this.deleteModal = true;
        },
        closeDeleteModal() {
            this.deleteModal = false;
        },
        save() {
            const self = this;
            if (!self.form.gcc_role_name || !self.form.user_role_id) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('The GCC role and the ticketing role are required').toString(),
                    type: 'error'
                });
                return;
            }
            if (self.form.position === null && !self.form.manager_user_role_id) {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An ambiguous role (without a fixed position) requires a manager role').toString(),
                    type: 'error'
                });
                return;
            }
            self.saving = true;
            const request = self.form.id
                ? axios.put('api/dashboard/admin/gcc-role-mappings/' + self.form.id, self.form)
                : axios.post('api/dashboard/admin/gcc-role-mappings', self.form);
            request.then(function (response) {
                self.saving = false;
                self.closeModal();
                self.getMappings();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: (response.data && response.data.message) ? response.data.message : self.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                self.saving = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: (error.response && error.response.data && error.response.data.message) ? error.response.data.message : self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        },
        destroy() {
            const self = this;
            self.deleting = true;
            axios.delete('api/dashboard/admin/gcc-role-mappings/' + self.form.id).then(function () {
                self.deleting = false;
                self.closeDeleteModal();
                self.getMappings();
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Mapping deleted successfully').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.deleting = false;
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$i18n.t('An error occurred while saving data').toString(),
                    type: 'error'
                });
            });
        }
    }
}
</script>
